import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  FileText,
  Edit3,
  Save,
  RotateCcw,
  Eye,
  Loader2,
  CheckCircle,
  AlertCircle,
  X,
  RefreshCw,
} from 'lucide-react';
import { adminAPI } from '@/services/api';
import ReactMarkdown from 'react-markdown';

// Page configuration with icons and colors
const PAGE_CONFIG = {
  'terms-of-use': {
    title: 'Terms of Use',
    icon: FileText,
    color: 'bg-blue-100 text-blue-600',
    description: 'Legal terms and conditions for using the platform'
  },
  'privacy-policy': {
    title: 'Privacy Policy',
    icon: FileText,
    color: 'bg-purple-100 text-purple-600',
    description: 'How we collect, use, and protect user data'
  },
  'refund-policy': {
    title: 'Refund & Cancellation',
    icon: FileText,
    color: 'bg-green-100 text-green-600',
    description: 'Refund and cancellation policies for purchases'
  },
  'articles': {
    title: 'Articles Page Intro',
    icon: FileText,
    color: 'bg-orange-100 text-orange-600',
    description: 'Introduction text for the articles page'
  }
};

export default function AdminContentManager() {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPage, setSelectedPage] = useState(null);
  const [editedContent, setEditedContent] = useState('');
  const [editedTitle, setEditedTitle] = useState('');
  const [editedMetaDesc, setEditedMetaDesc] = useState('');
  const [saving, setSaving] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [resetting, setResetting] = useState(false);

  useEffect(() => {
    fetchPages();
  }, []);

  const fetchPages = async () => {
    setLoading(true);
    try {
      const response = await adminAPI.getPages();
      setPages(response.data || []);
    } catch (error) {
      console.error('Error fetching pages:', error);
      toast.error('Failed to load pages');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPage = async (slug) => {
    try {
      const response = await adminAPI.getPage(slug);
      const page = response.data;
      setSelectedPage(page);
      setEditedContent(page.content || '');
      setEditedTitle(page.title || '');
      setEditedMetaDesc(page.meta_description || '');
      setPreviewMode(false);
    } catch (error) {
      console.error('Error fetching page:', error);
      toast.error('Failed to load page content');
    }
  };

  const handleSave = async () => {
    if (!selectedPage) return;
    
    setSaving(true);
    try {
      await adminAPI.updatePage(selectedPage.slug, {
        title: editedTitle,
        content: editedContent,
        meta_description: editedMetaDesc
      });
      
      toast.success('Page content saved successfully');
      
      // Refresh pages list
      fetchPages();
      
      // Update selected page
      setSelectedPage(prev => ({
        ...prev,
        title: editedTitle,
        content: editedContent,
        meta_description: editedMetaDesc
      }));
    } catch (error) {
      console.error('Error saving page:', error);
      toast.error('Failed to save page content');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!selectedPage) return;
    
    setResetting(true);
    try {
      await adminAPI.resetPage(selectedPage.slug);
      toast.success('Page reset to default content');
      setResetDialogOpen(false);
      
      // Reload the page content
      handleSelectPage(selectedPage.slug);
      fetchPages();
    } catch (error) {
      console.error('Error resetting page:', error);
      toast.error('Failed to reset page');
    } finally {
      setResetting(false);
    }
  };

  const hasUnsavedChanges = selectedPage && (
    editedContent !== selectedPage.content ||
    editedTitle !== selectedPage.title ||
    editedMetaDesc !== (selectedPage.meta_description || '')
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-navy-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-content-manager">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-navy-700">Content Manager</h1>
          <p className="text-slate-600">Manage static page content (Terms, Privacy, Refund policies)</p>
        </div>
        <Button
          variant="outline"
          onClick={fetchPages}
          className="border-slate-300"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Page List Sidebar */}
        <div className="lg:col-span-1">
          <Card className="border-slate-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Editable Pages</CardTitle>
              <CardDescription>Select a page to edit its content</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              {Object.entries(PAGE_CONFIG).map(([slug, config]) => {
                const page = pages.find(p => p.slug === slug);
                const isSelected = selectedPage?.slug === slug;
                const Icon = config.icon;
                
                return (
                  <button
                    key={slug}
                    onClick={() => handleSelectPage(slug)}
                    className={`w-full flex items-center gap-3 p-3 rounded-lg border-2 transition-all text-left ${
                      isSelected
                        ? 'border-navy-500 bg-navy-50'
                        : 'border-slate-200 hover:border-slate-300 bg-white'
                    }`}
                    data-testid={`page-select-${slug}`}
                  >
                    <div className={`p-2 rounded-lg ${config.color}`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`font-medium text-sm ${isSelected ? 'text-navy-700' : 'text-slate-700'}`}>
                        {config.title}
                      </p>
                      <p className="text-xs text-slate-500 truncate">
                        {config.description}
                      </p>
                    </div>
                    {page && (
                      <Badge variant="secondary" className="text-xs">
                        {page.is_default ? 'Default' : 'Custom'}
                      </Badge>
                    )}
                  </button>
                );
              })}
            </CardContent>
          </Card>
        </div>

        {/* Editor Area */}
        <div className="lg:col-span-2">
          {selectedPage ? (
            <Card className="border-slate-200">
              <CardHeader className="border-b border-slate-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${PAGE_CONFIG[selectedPage.slug]?.color || 'bg-slate-100'}`}>
                      <FileText className="w-5 h-5" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">
                        {PAGE_CONFIG[selectedPage.slug]?.title || selectedPage.title}
                      </CardTitle>
                      <CardDescription>
                        Last updated: {selectedPage.updated_at ? new Date(selectedPage.updated_at).toLocaleDateString() : 'Unknown'}
                      </CardDescription>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPreviewMode(!previewMode)}
                      className={previewMode ? 'bg-navy-50 border-navy-500' : ''}
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      {previewMode ? 'Edit' : 'Preview'}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setResetDialogOpen(true)}
                      className="text-orange-600 border-orange-300 hover:bg-orange-50"
                    >
                      <RotateCcw className="w-4 h-4 mr-1" />
                      Reset
                    </Button>
                  </div>
                </div>
                
                {hasUnsavedChanges && (
                  <Badge className="mt-3 bg-amber-100 text-amber-700 border-amber-300">
                    <AlertCircle className="w-3 h-3 mr-1" />
                    Unsaved changes
                  </Badge>
                )}
              </CardHeader>
              
              <CardContent className="p-6 space-y-4">
                {previewMode ? (
                  /* Preview Mode */
                  <div className="prose prose-slate max-w-none">
                    <h1>{editedTitle}</h1>
                    <ReactMarkdown>{editedContent}</ReactMarkdown>
                  </div>
                ) : (
                  /* Edit Mode */
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="title">Page Title</Label>
                      <Input
                        id="title"
                        value={editedTitle}
                        onChange={(e) => setEditedTitle(e.target.value)}
                        placeholder="Enter page title"
                        className="border-slate-300"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="meta_desc">Meta Description (SEO)</Label>
                      <Input
                        id="meta_desc"
                        value={editedMetaDesc}
                        onChange={(e) => setEditedMetaDesc(e.target.value)}
                        placeholder="Brief description for search engines"
                        className="border-slate-300"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="content">Content (Markdown supported)</Label>
                        <span className="text-xs text-slate-500">
                          {editedContent.length} characters
                        </span>
                      </div>
                      <Textarea
                        id="content"
                        value={editedContent}
                        onChange={(e) => setEditedContent(e.target.value)}
                        placeholder="Enter page content using Markdown formatting..."
                        className="min-h-[400px] font-mono text-sm border-slate-300"
                      />
                      <p className="text-xs text-slate-500">
                        Tip: Use # for headers, **bold**, *italic*, - for lists, [link](url) for links
                      </p>
                    </div>
                  </>
                )}
              </CardContent>
              
              {!previewMode && (
                <div className="border-t border-slate-200 p-4 flex justify-end gap-3">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setEditedContent(selectedPage.content || '');
                      setEditedTitle(selectedPage.title || '');
                      setEditedMetaDesc(selectedPage.meta_description || '');
                    }}
                    disabled={!hasUnsavedChanges}
                  >
                    <X className="w-4 h-4 mr-1" />
                    Discard Changes
                  </Button>
                  <Button
                    onClick={handleSave}
                    disabled={saving || !hasUnsavedChanges}
                    className="bg-navy-600 hover:bg-navy-700"
                    data-testid="save-page-btn"
                  >
                    {saving ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4 mr-2" />
                    )}
                    Save Changes
                  </Button>
                </div>
              )}
            </Card>
          ) : (
            <Card className="border-slate-200 border-dashed">
              <CardContent className="flex flex-col items-center justify-center py-16">
                <Edit3 className="w-12 h-12 text-slate-400 mb-4" />
                <h3 className="text-lg font-semibold text-navy-700 mb-2">
                  Select a Page to Edit
                </h3>
                <p className="text-slate-500 text-center max-w-md">
                  Choose a page from the sidebar to view and edit its content.
                  All changes are saved and reflected on the public site immediately.
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Reset Confirmation Dialog */}
      <Dialog open={resetDialogOpen} onOpenChange={setResetDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset to Default Content?</DialogTitle>
            <DialogDescription>
              This will replace the current content with the original default content.
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleReset}
              disabled={resetting}
            >
              {resetting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <RotateCcw className="w-4 h-4 mr-2" />
              )}
              Reset to Default
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
